import { useEffect } from 'react';

const parseScriptsFromHtml = (text: string) => {
	if (!/<script[\s>]/i.test(text)) return [];

	const element = document.createElement('div');
	element.innerHTML = text;

	const scripts = Array.from(element.querySelectorAll('script'));

	return scripts;
};

export const useLoadExternalScripts = ({
	contentId,
	contentName,
	text,
}: {
	text?: string;
	contentId?: string;
	contentName?: string;
} = {}) => {
	useEffect(() => {
		// Load scripts

		if (!text || !contentId || !contentName) return;

		let scripts: HTMLScriptElement[] = [];
		try {
			scripts = parseScriptsFromHtml(text);
		} catch (error) {
			console.error(
				`Failed to parse espot scripts from HTML for "${contentId}-${contentName}"`,
				error
			);
			return;
		}

		const loadedScripts = scripts.reduce((loadedScripts, scriptToLoad, index) => {
			const testId = `useLoadExternalScripts-${contentId}-${contentName}-script-${index}`;

			try {
				const src = scriptToLoad.getAttribute('src');
				const innerHTML = scriptToLoad.innerHTML;

				if (!src && !innerHTML) {
					console.warn(`src or innerText is empty for espot script with testId "${testId}"`);
					return loadedScripts;
				}

				const script = document.createElement('script');

				const attributes = scriptToLoad.attributes;
				// eslint-disable-next-line functional/no-loop-statement
				for (let i = 0; i < attributes.length; i++) {
					const attribute = attributes[i];
					script.setAttribute(attribute.name, attribute.value);
				}

				if (src) {
					script.src = src;
					script.async = true;
				}

				if (innerHTML) {
					// HACK: Silence Next.js error popup without silencing the error itself
					script.innerHTML = `try {
  ${innerHTML}
} catch (error) {
  console.error(\`Unhandled error in espot script with testId "${testId}"\`, error);
}`;
				}

				if (scriptToLoad.type) {
					script.type = scriptToLoad.type;
				}
				script.setAttribute('data-testid', testId);
				script.onerror = (error) => {
					console.error(`Failed to load espot script with testId "${testId}"`, error);
				};

				document.body.appendChild(script);
				loadedScripts.push(script);

				return loadedScripts;
			} catch (error) {
				console.error(
					`An error occurred while loading espot script with testId "${testId}"`,
					error
				);

				return loadedScripts;
			}
		}, [] as HTMLScriptElement[]);

		return () => {
			loadedScripts.forEach((scriptElement) => {
				scriptElement.remove();
			});
		};
	}, [contentId, contentName, text]);
};
